import { useEffect, useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { productsData } from '../data/products';
import './Products.css';

import garam_masala from '../assets/garam masala.jpg';
import curry_masala from '../assets/curry masala.jpg';
import luxury_range from '../assets/origano.jpg';
import masala_paste from '../assets/ginger garlic.jpg';
import farm_fresh from '../assets/turmeric.jpg';
import world_series from '../assets/butter chicken.jpg';
import sprinkles from '../assets/chilli flex.jpg';
import all_spices from '../assets/spices.jpg';

const categories = [
  { id: 'All', name: 'All Collection', img: all_spices },
  { id: 'Signature Spice Blends', name: 'Signature Spice Blends', img: garam_masala },
  { id: 'Chef’s Ready Mix', name: 'Chef’s Ready Mix', img: curry_masala },
  { id: 'Luxury Spice Range', name: 'Luxury Spice Range', img: luxury_range },
  { id: 'Rich Masala Pastes', name: 'Rich Masala Pastes', img: masala_paste },
  { id: 'Farm Fresh Spices', name: 'Farm Fresh Spices', img: farm_fresh },
  { id: 'World Spice Series', name: 'World Spice Series', img: world_series },
  { id: 'Savory Sprinkle Collection', name: 'Savory Sprinkle Collection', img: sprinkles }
];

const Products = () => {
  const [activeCategory, setActiveCategory] = useState('All');

  const filteredProducts = useMemo(() => {
    return activeCategory === 'All' 
      ? productsData 
      : productsData.filter(p => p.category === activeCategory);
  }, [activeCategory]);

  useEffect(() => {
    const container = document.getElementById('category-scroll-container');
    let scrollInterval;
    
    const startAutoScroll = () => {
      scrollInterval = setInterval(() => {
        if (container) {
          if (container.scrollLeft + container.clientWidth >= container.scrollWidth - 10) {
            container.scrollTo({ left: 0, behavior: 'smooth' });
          } else {
            container.scrollBy({ left: 1, behavior: 'auto' }); // Subtle constant crawl
          }
        }
      }, 30);
    };

    startAutoScroll();

    // Pause on hover
    if (container) {
      container.addEventListener('mouseenter', () => clearInterval(scrollInterval));
      container.addEventListener('mouseleave', startAutoScroll);
    }

    return () => clearInterval(scrollInterval);
  }, []);

  const scrollCategories = (direction) => {
    const container = document.getElementById('category-scroll-container');
    const scrollAmount = 400;
    if (direction === 'left') {
      container.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
    } else {
      container.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }
  };

  return (
    <div className="products-page">
      {/* INNOVATIVE SPLIT HERO */}
      <section className="products-hero-innovative">
        <div className="hero-split-grid">
          <div className="hero-visual-side">
            <motion.div 
              className="hero-image-stack"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1.2, ease: "easeOut" }}
            >
              <img src="https://images.unsplash.com/photo-1596040033229-a9821ebd058d?w=1200&q=80" alt="Spice Textures" className="main-spice-img" />
              <div className="floating-card glass-card">
                <span className="card-num">01</span>
                <h4>Pure Stone-Ground</h4>
                <p>Authentic heritage in every pinch.</p>
              </div>
            </motion.div>
          </div>

          <div className="hero-text-side">
            <motion.div 
              className="hero-content-wrap"
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 1, delay: 0.2 }}
            >
              <span className="premium-accent-tag">The Art of Spice</span>
              <h1 className="hero-big-title">Nature’s <br /><span className="gradient-text">Masterpieces</span></h1>
              <p className="hero-big-desc">Explore our curated collection of artisanal spices, crafted for those who seek the extraordinary in every meal.</p>
              <div className="hero-cta-group">
                <a href="#collection" className="btn-primary">Explore Collection</a>
                <Link to="/story" className="link-text-premium">Our Heritage →</Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ROUND CATEGORY SELECTOR */}
      <section className="category-explorer-section" id="collection">
        <div className="container">
          <div className="category-header-row">
            <h2 className="explorer-title">Explore <span>Categories</span></h2>
            <div className="scroll-controls">
              <button onClick={() => scrollCategories('left')} className="scroll-btn" aria-label="Scroll Left">←</button>
              <button onClick={() => scrollCategories('right')} className="scroll-btn" aria-label="Scroll Right">→</button>
            </div>
          </div>
          
          <div className="category-scroll-wrapper" id="category-scroll-container">
            {categories.map((cat, i) => (
              <motion.div
                key={cat.id}
                className={`category-item-round ${activeCategory === cat.id ? 'active' : ''}`}
                onClick={() => setActiveCategory(cat.id)}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
              >
                <div className="round-img-wrap">
                  <img src={cat.img} alt={cat.name} />
                </div>
                <span className="category-label">{cat.name}</span>
                {activeCategory === cat.id && (
                  <motion.div layoutId="cat-underline" className="active-indicator" />
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ARCHITECTURAL GRID */}
      <section className="products-showcase">
        <div className="container">
          <motion.div className="products-masonry" layout>
            <AnimatePresence mode="popLayout">
              {filteredProducts.map((product, i) => (
                <motion.div
                  key={product.id}
                  layout
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ duration: 0.6, delay: i * 0.05 }}
                  className="product-entry"
                >
                  <Link to={`/product/${product.id}`} className="product-entry-link">
                    <div className="entry-visual">
                      <img src={product.img} alt={product.name} />
                      <div className="entry-badge" style={{ backgroundColor: product.color }}>
                        {product.category}
                      </div>
                      <div className="entry-hover-info">
                        <span className="explore-label">View Story</span>
                      </div>
                    </div>
                    <div className="entry-details">
                      <h3 className="entry-name">{product.name}</h3>
                      <p className="entry-tagline">{product.tagline}</p>
                      <div className="entry-footer">
                        <span className="entry-price">{product.price}</span>
                        <span className="entry-btn">→</span>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </AnimatePresence>
          </motion.div>
        </div>
      </section>

      {/* Discovery CTA */}
      <section className="discovery-section">
        <div className="container">
          <div className="discovery-card">
            <div className="discovery-content">
              <h2>Taste the <span className="gradient-text">Legacy</span></h2>
              <p>Experience the difference of zero-adulteration, stone-ground purity in your own kitchen.</p>
              <Link to="/contact" className="btn-primary">Wholesale Inquiries</Link>
            </div>
            <div className="discovery-visual">
              <img src="https://images.unsplash.com/photo-1599909533730-f4ccda0a5200?w=800&q=80" alt="Spice Heritage" />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Products;
